import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Grid, Typography, Divider } from "@mui/material";

import { AdminTabs } from "../Admin";
import { handlePagingAll } from "../../../components/api/APIUtilities";
import GameSystemPicker from "../../../components/utilities/gamesystempickers/GameSystemPicker";
import ScorecardPicker from "../../../components/utilities/ScorecardPicker";
import MarkdownViewer from "../../../components/utilities/MarkdownViewer";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";
import { getEnvironmentVariable } from "../../../global/environmentVariables";
const SINGLE_GAME_MANUFACTURER = getEnvironmentVariable(
  "SINGLE_GAME_MANUFACTURER"
);

// BCP-411 support/admin tool: look up a scorecard and see both its public description (what
// TOs see) and its internal description (support/admin-only, seeded by BCP-410 from the
// scorecard's own bcpupdate:true fields). internalDescription is only ever returned by the API
// when the caller is an admin (site-wide or scoped to that scorecard's game system) - the
// backend, not this page, is what actually enforces who can see it.
//
// Follows the same two-stage picker pattern as the rest of /admin/settings (AdminGameSystems)
// and the existing scorecard picker on the event Game Configuration tab: pick a game system
// first, then fetch only that game system's scorecards - never loads every scorecard for
// every game system at once.
export default function AdminScorecards(props) {
  const { t } = useTranslation();
  const { activeTab, handleTabChange, authContext } = props;

  const [loading, setLoading] = useState(true);
  const [loadingScorecards, setLoadingScorecards] = useState(false);
  const [gameSystems, setGameSystems] = useState(null);
  const [gameSystem, setGameSystem] = useState(null);
  const [scorecards, setScorecards] = useState(null);
  const [scorecard, setScorecard] = useState(null);

  useEffect(() => {
    const go = async () => {
      let queryParams = {};
      if (SINGLE_GAME_MANUFACTURER) {
        queryParams.manufacturer = SINGLE_GAME_MANUFACTURER;
      }

      var apiGameSystems = await handlePagingAll("/gamesystems", queryParams);
      if (
        !authContext?.authState?.identity?.isAdmin &&
        authContext?.authState?.identity?.adminGameSystems?.length > 0
      ) {
        apiGameSystems = apiGameSystems.filter((el) =>
          authContext.authState.identity.adminGameSystems.includes(el.id)
        );
      }

      if (apiGameSystems?.length > 0) {
        apiGameSystems.sort((a, b) => {
          if (a.manufacturerOrderNum === b.manufacturerOrderNum) {
            return a.gameSystemOrderNum - b.gameSystemOrderNum;
          } else {
            return a.manufacturerOrderNum - b.manufacturerOrderNum;
          }
        });
      }

      setGameSystems(apiGameSystems);
      setLoading(false);
    };
    go(); // eslint-disable-next-line
  }, []);

  const selectGameSystem = async (gs) => {
    setGameSystem(gs);
    setScorecard(null);
    setScorecards(null);
    if (!gs) return;

    setLoadingScorecards(true);
    const apiScorecards = await handlePagingAll("/scorecards", {
      gameSystemId: gs.id
    });

    // ScorecardPicker renders scorecard.value/scorecard.label (see ScorecardPicker.js) - the
    // raw API records only have .id/.name, so without this every option renders blank.
    const pickerItems = (apiScorecards || []).map((sc) => ({
      ...sc,
      value: sc.id,
      label: sc.name
    }));

    setScorecards(pickerItems);
    setLoadingScorecards(false);
  };

  return (
    <>
      {Boolean(loading) && <ModalLoadingFull />}
      <Grid container sx={{ gap: 2, p: 2 }}>
        <Grid
          item
          xs={12}
          sx={{
            p: 2,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 1,
            backgroundColor: "background.secondary"
          }}
        >
          <Typography variant="h3">{t("pages.admin.adminSettings")}</Typography>

          <AdminTabs
            activeTab={activeTab}
            handleTabChange={handleTabChange}
            authContext={authContext}
          />
        </Grid>

        {gameSystems && (
          <Grid
            item
            xs={12}
            sx={{
              p: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              borderRadius: 1,
              boxShadow: 1,
              backgroundColor: "background.secondary"
            }}
          >
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <GameSystemPicker
                  name="gameType"
                  value={gameSystem?.code || ""}
                  handleChange={(e) => {
                    if (e.target.value !== undefined) {
                      const found = gameSystems.find(
                        (gs) => gs.code === e.target.value
                      );
                      selectGameSystem(found || null);
                    }
                  }}
                  gameSystems={gameSystems}
                />
              </Grid>
            </Grid>
          </Grid>
        )}

        {gameSystem && (
          <Grid
            item
            xs={12}
            sx={{
              p: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              borderRadius: 1,
              boxShadow: 1,
              backgroundColor: "background.secondary"
            }}
          >
            {Boolean(loadingScorecards) && <ModalLoadingFull />}
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <ScorecardPicker
                  name="scorecardId"
                  label="Scorecard"
                  value={scorecard?.id || ""}
                  handleChange={(e) => {
                    const found = scorecards?.find(
                      (sc) => sc.value === e.target.value
                    );
                    setScorecard(found || null);
                  }}
                  scorecards={scorecards}
                  inclAny={false}
                  clearable
                  clearLabel="Clear"
                />
              </Grid>
            </Grid>
          </Grid>
        )}

        {scorecard && (
          <Grid
            item
            xs={12}
            sx={{
              p: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              borderRadius: 1,
              boxShadow: 1,
              backgroundColor: "background.secondary"
            }}
          >
            <Typography variant="h5" gutterBottom>
              {scorecard.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {gameSystem?.name} &middot; {scorecard.type}
              {scorecard.scorecardType?.label
                ? ` · ${scorecard.scorecardType.label}`
                : ""}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight="700" gutterBottom>
              Public Description
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", mb: 2 }}>
              {scorecard.publicDescription ||
                "No description available for this scorecard yet."}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight="700" gutterBottom>
              Internal Description (support/admin only)
            </Typography>
            {scorecard.internalDescription ? (
              <MarkdownViewer source={scorecard.internalDescription} />
            ) : (
              <Typography variant="body1" color="text.secondary">
                No internal description available for this scorecard yet.
              </Typography>
            )}
          </Grid>
        )}
      </Grid>
    </>
  );
}
